import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowUpRight, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

const PortfolioSection = () => {
  const { t } = useLanguage();
  const [wishioSlide, setWishioSlide] = useState(0);
  const wishioVideoRefs = useRef([]);
  const expeditioVideoRef = useRef(null);
  const jasonVideoRef = useRef(null);

  const wishioVideos = [
    {
      src: "/assets/wishio_birthday_reminder.mp4",
      label: "Birthday Reminder"
    },
    {
      src: "/assets/wishio_birthday_gifts.mp4",
      label: "Birthday Gifts"
    }
  ];

  const expeditioVideo = "/assets/expeditio.mp4";
  const expeditioSiteUrl = "https://expeditio.app";

  const jasonVideo = "/assets/jason_intro.mp4";

  // Handle Wishio video playback when slide changes
  useEffect(() => {
    wishioVideoRefs.current.forEach((video, index) => {
      if (video) {
        if (index === wishioSlide) {
          video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  }, [wishioSlide]);

  const nextWishioSlide = () => {
    setWishioSlide((prev) => (prev + 1) % wishioVideos.length);
  };

  const prevWishioSlide = () => {
    setWishioSlide((prev) => (prev - 1 + wishioVideos.length) % wishioVideos.length);
  };

  // iPhone Mockup Component
  const IPhoneMockup = ({ children, className = "" }) => (
    <div className={`relative ${className}`}>
      <div className="relative w-[220px] h-[450px] sm:w-[260px] sm:h-[530px] lg:w-[280px] lg:h-[570px] bg-gradient-to-b from-zinc-800 to-zinc-900 rounded-[3rem] p-[4px] shadow-2xl shadow-black/50">
        <div className="w-full h-full bg-black rounded-[2.8rem] overflow-hidden relative">
          {/* Dynamic Island */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 w-[90px] h-[28px] sm:w-[100px] sm:h-[30px] lg:w-[110px] lg:h-[32px] bg-black rounded-full z-20 flex items-center justify-center">
            <div className="absolute right-4 w-2 h-2 rounded-full bg-zinc-900 ring-1 ring-zinc-800" />
          </div>
          
          {children}
          
          {/* Home indicator */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[100px] lg:w-[120px] h-1 bg-zinc-700 rounded-full z-20" />
        </div>
        
        {/* Side buttons */}
        <div className="absolute right-[-3px] top-28 w-[4px] h-16 bg-zinc-700 rounded-l-sm" />
        <div className="absolute left-[-3px] top-24 w-[4px] h-8 bg-zinc-700 rounded-r-sm" />
        <div className="absolute left-[-3px] top-36 w-[4px] h-14 bg-zinc-700 rounded-r-sm" />
        <div className="absolute left-[-3px] top-20 w-[4px] h-4 bg-zinc-700 rounded-r-sm" />
      </div>
      
      {/* Glow effect */}
      <div className="absolute -inset-8 bg-cyan-500/10 rounded-full blur-3xl opacity-50" />
    </div>
  );

  return (
    <section id="portfolio" className="py-32 lg:py-40 bg-black relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-1/4 left-0 w-px h-64 bg-gradient-to-b from-transparent via-zinc-800 to-transparent" />
      <div className="absolute bottom-1/4 right-0 w-px h-64 bg-gradient-to-b from-transparent via-zinc-800 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mb-20">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-cyan-400 to-transparent" />
            <span className="text-xs tracking-[0.3em] text-cyan-400 uppercase font-medium">Work</span>
          </div>
          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-extralight text-white mb-8 leading-tight">
            {t.portfolio.title}
          </h2>
          <p className="text-lg text-zinc-500 font-light">
            {t.portfolio.subtitle}
          </p>
        </div>

        {/* Portfolio Grid */}
        <div className="grid lg:grid-cols-2 gap-1">
          {/* Wishio Project Card */}
          <div className="group relative bg-zinc-950 border border-zinc-900 overflow-hidden">
            {/* iPhone Carousel */}
            <div className="aspect-[3/4] sm:aspect-[4/5] lg:aspect-[3/4] relative overflow-hidden bg-gradient-to-br from-zinc-900 via-zinc-900 to-black">
              {/* Decorative grid background */}
              <div 
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                  backgroundSize: '30px 30px'
                }}
              />
              
              {/* iPhone Carousel Container */}
              <div className="absolute inset-0 flex items-center justify-center px-4">
                {/* Navigation Arrow - Left */}
                <button 
                  onClick={prevWishioSlide}
                  className="absolute left-4 lg:left-8 z-30 w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center bg-zinc-900/80 border border-zinc-700 hover:border-cyan-500/50 hover:bg-zinc-800 rounded-full transition-all duration-300 backdrop-blur-sm"
                  data-testid="wishio-carousel-prev-btn"
                >
                  <ChevronLeft className="w-5 h-5 lg:w-6 lg:h-6 text-zinc-400" />
                </button>

                <IPhoneMockup>
                  <div className="relative w-full h-full">
                    {wishioVideos.map((video, index) => (
                      <video 
                        key={index}
                        ref={el => wishioVideoRefs.current[index] = el}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                          index === wishioSlide ? 'opacity-100' : 'opacity-0'
                        }`}
                        loop 
                        muted 
                        playsInline
                        autoPlay={index === 0}
                        data-testid={`wishio-video-${index}`}
                      >
                        <source src={video.src} type="video/mp4" />
                      </video>
                    ))}
                  </div>
                </IPhoneMockup>

                {/* Navigation Arrow - Right */}
                <button 
                  onClick={nextWishioSlide}
                  className="absolute right-4 lg:right-8 z-30 w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center bg-zinc-900/80 border border-zinc-700 hover:border-cyan-500/50 hover:bg-zinc-800 rounded-full transition-all duration-300 backdrop-blur-sm"
                  data-testid="wishio-carousel-next-btn"
                >
                  <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6 text-zinc-400" />
                </button>
              </div>

              {/* Slide Indicators */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 z-20">
                {wishioVideos.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setWishioSlide(index)}
                    className={`transition-all duration-300 ${
                      index === wishioSlide 
                        ? 'w-8 h-2 bg-cyan-400 rounded-full' 
                        : 'w-2 h-2 bg-zinc-600 hover:bg-zinc-500 rounded-full'
                    }`}
                    data-testid={`wishio-indicator-${index}`}
                  />
                ))}
              </div>
              
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* Project Content */}
            <div className="p-8 lg:p-10 relative">
              <div className="flex items-start justify-between mb-6">
                <h3 className="text-2xl lg:text-3xl font-light text-white">
                  {t.portfolio.wishio.title}
                </h3>
                <a 
                  href="https://wishio.site" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center border border-zinc-800 hover:border-cyan-500/50 hover:bg-cyan-500/10 transition-all duration-500"
                  data-testid="wishio-external-link"
                >
                  <ArrowUpRight className="w-4 h-4 text-zinc-600 hover:text-cyan-400 transition-colors" />
                </a>
              </div>
              
              <p className="text-zinc-500 leading-relaxed mb-6">
                {t.portfolio.wishio.description}
              </p>

              {/* Visit Site Link */}
              <a 
                href="https://wishio.site" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors mb-8"
                data-testid="wishio-site-link"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="text-sm tracking-wide">wishio.site</span>
              </a>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {t.portfolio.wishio.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="px-4 py-2 text-xs text-zinc-400 bg-zinc-900 border border-zinc-800 tracking-wider"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Expeditio Project Card */}
          <div className="group relative bg-zinc-950 border border-zinc-900 overflow-hidden">
            {/* iPhone with Video */}
            <div className="aspect-[3/4] sm:aspect-[4/5] lg:aspect-[3/4] relative overflow-hidden bg-gradient-to-br from-zinc-900 via-zinc-900 to-black">
              {/* Decorative grid background */}
              <div 
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                  backgroundSize: '30px 30px'
                }}
              />
              
              {/* iPhone Container */}
              <div className="absolute inset-0 flex items-center justify-center px-4">
                <IPhoneMockup>
                  <video 
                    ref={expeditioVideoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    loop 
                    muted 
                    playsInline
                    autoPlay
                    data-testid="expeditio-video"
                  >
                    <source src={expeditioVideo} type="video/mp4" />
                  </video>
                </IPhoneMockup>
              </div>

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* Project Content */}
            <div className="p-8 lg:p-10 relative">
              <div className="flex items-start justify-between mb-6">
                <h3 className="text-2xl lg:text-3xl font-light text-white">
                  {t.portfolio.expeditio?.title || "Expeditio"}
                </h3>
                <a 
                  href={expeditioSiteUrl}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center border border-zinc-800 hover:border-cyan-500/50 hover:bg-cyan-500/10 transition-all duration-500"
                  data-testid="expeditio-external-link"
                >
                  <ArrowUpRight className="w-4 h-4 text-zinc-600 hover:text-cyan-400 transition-colors" />
                </a>
              </div>
              
              <p className="text-zinc-500 leading-relaxed mb-6">
                {t.portfolio.expeditio?.description || "AI-powered travel planning app with offline maps, document storage, photo organization, smart hotel suggestions, and a community for tips and reviews."}
              </p>

              <a 
                href={expeditioSiteUrl}
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors mb-8"
                data-testid="expeditio-site-link"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="text-sm tracking-wide">expeditio.app</span>
              </a>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {(t.portfolio.expeditio?.tags || ["iOS", "Android", "AI", "Travel"]).map((tag, index) => (
                  <span 
                    key={index}
                    className="px-4 py-2 text-xs text-zinc-400 bg-zinc-900 border border-zinc-800 tracking-wider"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Jason and the Argonauts Project Card */}
          <div className="group relative bg-zinc-950 border border-zinc-900 overflow-hidden">
            {/* iPhone with Video */}
            <div className="aspect-[3/4] sm:aspect-[4/5] lg:aspect-[3/4] relative overflow-hidden bg-gradient-to-br from-zinc-900 via-zinc-900 to-black">
              {/* Decorative grid background */}
              <div 
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                  backgroundSize: '30px 30px'
                }}
              />

              {/* iPhone Container */}
              <div className="absolute inset-0 flex items-center justify-center px-4">
                <IPhoneMockup>
                  <video 
                    ref={jasonVideoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    loop 
                    muted 
                    playsInline
                    autoPlay
                    data-testid="jason-video"
                  >
                    <source src={jasonVideo} type="video/mp4" />
                  </video>
                </IPhoneMockup>
              </div>

              {/* "In Development" Badge */}
              <div className="absolute top-6 right-6 z-20">
                <span className="px-3 py-1.5 text-xs font-medium tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-full">
                  {t.portfolio.jason?.badge || "IN DEVELOPMENT"}
                </span>
              </div>

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* Project Content */}
            <div className="p-8 lg:p-10 relative">
              <h3 className="text-2xl lg:text-3xl font-light text-white mb-6">
                {t.portfolio.jason?.title || "Jason and the Argonauts"}
              </h3>

              <p className="text-zinc-500 leading-relaxed mb-8">
                {t.portfolio.jason?.description || "Single-player mythological expedition strategy-RPG for iOS, Android and web. A procedurally generated Aegean, a deterministic game engine, and applied AI that renders narration without ever owning game state."}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {(t.portfolio.jason?.tags || ["iOS", "Android", "Web", "Game Engine", "Applied AI"]).map((tag, index) => (
                  <span 
                    key={index}
                    className="px-4 py-2 text-xs text-zinc-400 bg-zinc-900 border border-zinc-800 tracking-wider"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
